<template>
  <AccountContextWrapper>
    <template #default>
      <section class="budget-tracking-index mt-3">
        <header
          class="d-flex justify-content-between align-items-start gap-2 mb-3"
        >
          <div class="text-start">
            <h2 class="h5 mb-2">Tracking</h2>
            <p class="mb-0 text-muted">
              Track budget performance against real spending and surface areas
              that need attention.
            </p>
          </div>
          <BButton
            variant="outline-primary"
            :disabled="loadingSnapshots || loadingTracking"
            @click="refreshData"
          >
            Refresh
          </BButton>
        </header>

        <div v-if="loadingSnapshots" class="text-center my-4">
          <BSpinner label="Loading snapshots..." />
        </div>

        <div v-else-if="snapshotLoadError" class="alert alert-danger">
          Unable to load budget snapshots for this account.
        </div>

        <div
          v-else-if="availableSnapshots.length === 0"
          class="text-center my-4"
        >
          <IconAlertTriangle class="me-1 text-warning" />
          You haven't created any budget snapshots yet.
          <div class="mt-2">
            <BButton variant="primary" @click="goToCreateSnapshot">
              Create your first budget snapshot
            </BButton>
          </div>
        </div>

        <template v-else>
          <BFormGroup
            label="Snapshot"
            label-for="snapshot-selector"
            class="mb-3"
          >
            <BFormSelect
              id="snapshot-selector"
              v-model="selectedSnapshotUid"
              :options="snapshotOptions"
            />
          </BFormGroup>

          <div v-if="loadingTracking" class="text-center my-4">
            <BSpinner label="Loading tracking data..." />
          </div>

          <div v-else-if="trackingLoadError" class="alert alert-danger mb-3">
            Unable to load tracking transactions. Try refresh.
          </div>

          <div v-else-if="selectedSnapshot" class="row g-3 align-items-start">
            <div class="col-12 col-lg-4">
              <BudgetOverview
                :selected-snapshot="selectedSnapshot"
                :overview-rows="overviewRows"
              />
            </div>
            <div class="col-12 col-lg-8">
              <TransactionsView
                :financial-transactions="financialTransactions"
                :categorized-by-plaid-transaction-uid="
                  categorizedByPlaidTransactionUid
                "
                :selected-snapshot-uid="selectedSnapshotUid"
                :saving-tracking="savingTracking"
                :category-options="categoryOptions"
                :snapshot-categories="snapshotCategories"
                :drafts-by-transaction-id="draftsByTransactionId"
                @save-all-changes="saveAllChanges"
                @add-draft="addDraft"
                @update-draft="handleUpdateDraft"
                @remove-draft="handleRemoveDraft"
              />
            </div>
          </div>
        </template>
      </section>
    </template>
  </AccountContextWrapper>
</template>

<script>
import { BButton, BFormGroup, BFormSelect, BSpinner } from "bootstrap-vue-next";
import { IconAlertTriangle } from "@tabler/icons-vue";
import AccountContextWrapper from "@/components/molecules/AccountContextWrapper.vue";
import BudgetOverview from "@/components/Budget/BudgetTracking/BudgetOverview.vue";
import TransactionsView from "@/components/Budget/BudgetTracking/TransactionsView.vue";
import {
  budgetSnapshots as budgetSnapshotsNs,
  budgetTracking as budgetTrackingNs,
  userAccount as userAcctNs,
  userSession as userSessNs,
} from "@/store/namespaces";

export default {
  name: "BudgetTrackingIndexView",
  components: {
    AccountContextWrapper,
    BButton,
    BFormGroup,
    BFormSelect,
    BSpinner,
    BudgetOverview,
    IconAlertTriangle,
    TransactionsView,
  },
  data() {
    return {
      selectedSnapshotUid: null,
      draftsByTransactionId: {},
    };
  },
  computed: {
    acctUid() {
      return this.$store.getters[`${userAcctNs}/getSelectedAcctUid`];
    },
    availableSnapshots() {
      return (
        this.$store.getters[`${budgetSnapshotsNs}/getSnapshotsByAcctUid`](
          this.acctUid
        ) || []
      );
    },
    loadingSnapshots() {
      return this.$store.getters[`${budgetSnapshotsNs}/getLoadingByAcctUid`](
        this.acctUid
      );
    },
    snapshotLoadError() {
      return this.$store.getters[`${budgetSnapshotsNs}/getErrorByAcctUid`](
        this.acctUid
      );
    },
    loadingTracking() {
      return this.$store.getters[`${budgetTrackingNs}/getLoadingByAcctUid`](
        this.acctUid
      );
    },
    trackingLoadError() {
      return this.$store.getters[`${budgetTrackingNs}/getErrorByAcctUid`](
        this.acctUid
      );
    },
    savingTracking() {
      return this.$store.getters[`${budgetTrackingNs}/getSavingByAcctUid`](
        this.acctUid
      );
    },
    snapshotOptions() {
      const options = [...this.availableSnapshots]
        .sort(
          (a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0)
        )
        .map((snapshot) => ({
          value: snapshot.snapshot_uid,
          text: `${snapshot.alias} (${snapshot.updated_at || "n/a"})`,
        }));

      return [{ value: null, text: "Select a snapshot" }, ...options];
    },
    selectedSnapshot() {
      if (!this.selectedSnapshotUid) {
        return null;
      }

      return (
        this.availableSnapshots.find(
          (snapshot) => snapshot.snapshot_uid === this.selectedSnapshotUid
        ) || null
      );
    },
    snapshotCategories() {
      return this.selectedSnapshot?.categories || [];
    },
    categoryOptions() {
      const options = this.snapshotCategories.map((category) => ({
        value: category.category_uid || category.categoryUid,
        text: category.name,
      }));

      return [{ value: null, text: "Select category" }, ...options];
    },
    financialTransactions() {
      return (
        this.$store.getters[
          `${budgetTrackingNs}/getFinancialTransactionsByAcctUid`
        ](this.acctUid) || []
      );
    },
    categorizedTransactions() {
      return (
        this.$store.getters[
          `${budgetTrackingNs}/getCategorizedTransactionsByAcctUid`
        ](this.acctUid) || []
      );
    },
    categorizedTransactionsForSnapshot() {
      if (!this.selectedSnapshotUid) {
        return [];
      }

      return this.categorizedTransactions.filter(
        (item) => item.snapshot_uid === this.selectedSnapshotUid
      );
    },
    categorizedByPlaidTransactionUid() {
      return this.categorizedTransactionsForSnapshot.reduce((acc, item) => {
        const key = item.plaid_transaction_uid;
        if (!acc[key]) {
          acc[key] = [];
        }

        acc[key].push(item);
        return acc;
      }, {});
    },
    overviewRows() {
      if (!this.selectedSnapshot) {
        return [];
      }

      return this.snapshotCategories.map((category) => {
        const categoryUid = category.category_uid || category.categoryUid;
        const spent = this.categorizedTransactionsForSnapshot
          .filter((item) => item.spending_category_uid === categoryUid)
          .reduce((sum, item) => sum + Number(item.fixed_amount || 0), 0);

        const budget = Number(
          category.budget_amount ?? category.budgetAmount ?? 0
        );

        const remaining = budget - spent;
        const spentPct = budget > 0 ? (spent / budget) * 100 : 0;

        return {
          categoryUid,
          name: category.name,
          spent,
          budget,
          remaining,
          spentPct,
        };
      });
    },
    drafts() {
      return Object.values(this.draftsByTransactionId).flat();
    },
  },
  watch: {
    acctUid: {
      immediate: true,
      async handler(nextAcctUid) {
        if (!nextAcctUid) {
          return;
        }

        await this.loadInitialData(nextAcctUid);
      },
    },
    availableSnapshots: {
      immediate: true,
      handler(snapshots) {
        if (!snapshots || snapshots.length === 0) {
          this.selectedSnapshotUid = null;
          return;
        }

        const selectedStillExists = snapshots.some(
          (snapshot) => snapshot.snapshot_uid === this.selectedSnapshotUid
        );

        if (!selectedStillExists) {
          this.selectedSnapshotUid = snapshots[0].snapshot_uid;
        }
      },
    },
  },
  methods: {
    async loadInitialData(acctUid) {
      await this.$store.dispatch(
        `${budgetSnapshotsNs}/fetchBudgetSnapshotsByAcctUid`,
        {
          acctUid,
          force: false,
        }
      );

      await this.$store.dispatch(`${budgetTrackingNs}/fetchTrackingData`, {
        acctUid,
        force: false,
      });
    },
    async refreshData() {
      if (!this.acctUid) {
        return;
      }

      await Promise.all([
        this.$store.dispatch(
          `${budgetSnapshotsNs}/fetchBudgetSnapshotsByAcctUid`,
          {
            acctUid: this.acctUid,
            force: true,
          }
        ),
        this.$store.dispatch(`${budgetTrackingNs}/fetchTrackingData`, {
          acctUid: this.acctUid,
          force: true,
        }),
      ]);
    },
    goToCreateSnapshot() {
      this.$router.push({ path: "/budget/snapshots/create" });
    },
    draftsForTransaction(transactionId) {
      return this.draftsByTransactionId[transactionId] || [];
    },
    addDraft(row) {
      const transactionId = row.transaction_id;
      const currentDrafts = this.draftsForTransaction(transactionId);
      const defaultCategory = this.categoryOptions[1]?.value || null;

      this.draftsByTransactionId = {
        ...this.draftsByTransactionId,
        [transactionId]: [
          ...currentDrafts,
          {
            localId: `${transactionId}-${Date.now()}-${currentDrafts.length}`,
            spendingCategoryUid: defaultCategory,
            fixedAmount: "",
            plaidTransactionUid: row.transaction_id,
            plaidEffectiveDate: row.effective_date,
          },
        ],
      };
    },
    updateDraft(transactionId, localId, key, value) {
      const drafts = this.draftsForTransaction(transactionId).map((draft) => {
        if (draft.localId !== localId) {
          return draft;
        }

        return {
          ...draft,
          [key]: value,
        };
      });

      this.draftsByTransactionId = {
        ...this.draftsByTransactionId,
        [transactionId]: drafts,
      };
    },
    handleUpdateDraft({ transactionId, localId, key, value }) {
      this.updateDraft(transactionId, localId, key, value);
    },
    removeDraft(transactionId, localId) {
      const drafts = this.draftsForTransaction(transactionId).filter(
        (draft) => draft.localId !== localId
      );

      const nextDrafts = {
        ...this.draftsByTransactionId,
      };

      if (drafts.length === 0) {
        delete nextDrafts[transactionId];
      } else {
        nextDrafts[transactionId] = drafts;
      }

      this.draftsByTransactionId = nextDrafts;
    },
    handleRemoveDraft({ transactionId, localId }) {
      this.removeDraft(transactionId, localId);
    },
    async saveAllChanges() {
      if (
        this.savingTracking ||
        this.drafts.length === 0 ||
        !this.selectedSnapshotUid
      ) {
        return;
      }

      const userInfo = await this.$store.dispatch(`${userSessNs}/getUserInfo`);
      const userId = userInfo?.id;

      if (!userId) {
        this.$notify.notifyError("Unable to resolve user id for saving.");
        return;
      }

      const drafts = this.drafts.map((draft) => ({
        ...draft,
        fixedAmount: Number(draft.fixedAmount),
      }));

      const { successCount, failed } = await this.$store.dispatch(
        `${budgetTrackingNs}/saveCategorizedTransactionsBatch`,
        {
          acctUid: this.acctUid,
          userId,
          snapshotUid: this.selectedSnapshotUid,
          drafts,
        }
      );

      if (failed.length === 0) {
        this.draftsByTransactionId = {};
        this.$notify.notifySuccess(
          `${successCount} categorized transaction(s) saved successfully.`
        );
        return;
      }

      this.$notify.notifyError(
        `${failed.length} transaction(s) failed to save. ${successCount} succeeded.`
      );
    },
  },
};
</script>

<style scoped>
.budget-tracking-index {
  width: 100%;
}
</style>
